import React, { useId, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '@dashboard/components/ui/button';
import PageFilesForm from '../PageFilesForm';
import EventMediaForm from '../../scenes/PageEvent/components/EventMediaForm';
import FormImageGalleryForm from '../../scenes/PageForm/components/FormImageGalleryForm';
import ProductFilesForm from '../../scenes/PageProduct/components/ProductFilesForm';
import ProductMediaForm from '../../scenes/PageProduct/components/ProductMediaForm';
import { UPLOAD_LABELS, UPLOAD_TITLES, uploadSection } from './aiUploads';

// The editor's own Image gallery and File downloads forms, so an upload here is the same as one made there.
const UPLOADERS = {
  images: { checkout: ProductMediaForm, event: EventMediaForm, form: FormImageGalleryForm },
  files: { checkout: ProductFilesForm, form: PageFilesForm },
};

const uploaderOf = (kind, pageType) =>
  Object.hasOwn(UPLOADERS, kind) ? UPLOADERS[kind][pageType] : undefined;

export function canUpload(kind, pageType) {
  return Boolean(uploaderOf(kind, pageType) && uploadSection(kind, pageType));
}

export function UploadPanel({ kind, pageType, onOpen }) {
  const Uploader = uploaderOf(kind, pageType);
  const section = uploadSection(kind, pageType);
  if (!Uploader || !section) return null;

  return (
    // The chat is about 220px wide on a 1024px screen: the forms' /upload variants compact them under 14rem.
    <div className="@container/upload flex min-w-0 flex-col gap-2 wrap-anywhere">
      <Uploader />
      {onOpen && (
        <Button
          variant="outline"
          size="xs"
          className="h-auto min-h-6 max-w-full self-end whitespace-normal py-0.5 text-left"
          onClick={() => onOpen({ route: section.route })}
        >
          Open {section.label}
          <ArrowRight aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}

// Open on the live latest turn only, so a long chat isn't a column of dropzones; the seller's own toggle sticks.
export default function UploadCard({ upload, pageType, defaultOpen, onOpen }) {
  const [toggled, setToggled] = useState(null);
  const panelId = useId();
  if (!canUpload(upload.kind, pageType)) return null;
  const title = UPLOAD_TITLES[upload.kind];
  const isOpen = toggled ?? defaultOpen;

  return (
    <div
      role="group"
      aria-label={title}
      className="flex flex-col gap-2.5 rounded-[10px] border bg-background p-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
        <div className="min-w-0 flex-1 basis-32">
          <div className="text-[13px] font-semibold leading-[19px]">{title}</div>
          {upload.note && (
            <p className="mt-0.5 whitespace-pre-wrap break-words text-[13px] leading-[19px] text-muted-foreground">
              {upload.note}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto min-h-7 max-w-full whitespace-normal text-left text-foreground"
          aria-expanded={isOpen}
          aria-controls={isOpen ? panelId : undefined}
          onClick={() => setToggled(!isOpen)}
        >
          {UPLOAD_LABELS[upload.kind]}
        </Button>
      </div>
      {isOpen && (
        <div id={panelId}>
          <UploadPanel kind={upload.kind} pageType={pageType} onOpen={onOpen} />
        </div>
      )}
    </div>
  );
}
